import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';

import { transformApolloErr } from '../utils/apolloErrorsTransformer';
import { notification } from './../utils/notification';
import {
    doRefreshMutation,
    getToken,
    isLoggedIn,
    isTokenExpired,
    logout,
    parseJwtToken,
} from './authorization';
import { addRuntimeErrorListeners, logServerError } from './logging';

const env = process.env.REACT_APP_ENV;

let BASE_URI = `${window.location.protocol}//${window.location.hostname}`;
let API_URI = BASE_URI;
// let GRAPHQL_URI; // Moved to const definition below
let RABBIT_URI = `${BASE_URI}:15672`;
let SEARCHKIT_URI = `${BASE_URI}:4340`;
let KIBANA_URI = `${BASE_URI}:9200/_plugin/kibana/app/kibana`;

switch (env) {
    case 'integration':
    case 'staging':
    case 'devtest':
    case 'devtest2':
        API_URI = `https://ps-${env}.isarbits.net`;
        BASE_URI = `https://ps-${env}.isarbits.net`;
        RABBIT_URI = `https://ps-dev-rabbit.isarbits.net`;
        SEARCHKIT_URI = `https://ps-${env}-logs.isarbits.net`;
        KIBANA_URI = 'https://ps-dev-es.isarbits.net/_plugin/kibana';
        break;
    case 'serverhosted':
    case 'production':
        KIBANA_URI = '';
        if (/\.isarbits\.net/.test(window.location.hostname)) {
            RABBIT_URI = `${window.location.protocol}//ps-prod-rabbit.${window.location.hostname}`;
            SEARCHKIT_URI = `${window.location.protocol}//ps-prod-logs.${window.location.hostname}`;
        } else {
            RABBIT_URI = `https://ps-prod-rabbit.isarbits.net`;
            SEARCHKIT_URI = `https://ps-prod-logs.isarbits.net`;
            KIBANA_URI = 'https://ps-prod-es.isarbits.net/_plugin/kibana';
        }

        break;
    default:
        API_URI = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
            ? `${BASE_URI}:4007` 
            : `${BASE_URI}`;
        break;
}

const GRAPHQL_URI = `${API_URI}/graphql`;

const JENKINS_URI = 'https://ps-j.isarbits.net';
const S3_PROXY_URI = `${API_URI}/api/s3-proxy`;
const SPINLOG_URI = `${SEARCHKIT_URI}/#/spindex`;

if (process.env.NODE_ENV === 'development') {
    console.info('Using GraphQL server at ', GRAPHQL_URI);
}

addRuntimeErrorListeners();

const errorLink = onError((e) => {
    if (process.env.NODE_ENV === 'production') {
        logServerError(e);
    }

    transformApolloErr(e).forEach(({ message, code }) => {
        notification.error(message);
        if (code === 'UNAUTHENTICATED') {
            logout(true);
        }
    });
});

const httpLink = createHttpLink({
    uri: GRAPHQL_URI,
});

const cache = new InMemoryCache();

export const unauthorizedClient = new ApolloClient({
    link: from([errorLink, httpLink]),
    cache,
});

let refreshingToken = null;

const authLink = setContext(async (_operation, { headers }) => {
    if (!isLoggedIn()) {
        return { headers };
    }

    if (isTokenExpired()) {
        if (!refreshingToken) {
            refreshingToken = doRefreshMutation();
        }
        await refreshingToken;
        refreshingToken = null;
    }

    const token = getToken();
    const user = parseJwtToken();
    if (!user || typeof user.id === 'undefined') {
        await logout(true);
        return { headers }; // Return early to avoid further errors, though page will reload
    }

    return {
        headers: {
            ...headers,
            Authorization: `Bearer ${token.accessToken}`,
        },
    };
});

export const client = new ApolloClient({
    link: from([authLink, errorLink, httpLink]),
    cache,
});

export {
    BASE_URI,
    API_URI,
    JENKINS_URI,
    S3_PROXY_URI,
    RABBIT_URI,
    SEARCHKIT_URI,
    SPINLOG_URI,
    KIBANA_URI,
};
